import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Download, Play, Plus, Share2, Shield, Trash2, UserCircle, X } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl, formatDate } from '@/utils/helpers';
import PlayerPhotoCrop from './PlayerPhotoCrop';

const AdminClubsManager = ({ leagues }) => {
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showClubForm, setShowClubForm] = useState(false);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [showMatchControl, setShowMatchControl] = useState(false);
  const [activeMatch, setActiveMatch] = useState(null);
  const [generatedGraphic, setGeneratedGraphic] = useState(null);
  
  // Sport Configuration
  const SPORT_CONFIGS = {
    soccer: {
      name: 'Soccer',
      halfLength: 45,
      periods: ['1H', 'HT', '2H', 'ET1', 'ET2', 'PEN'],
      extraTimeStart: 90,
      scoring: [{ type: 'goal', label: 'Goal', points: 1, emoji: '⚽' }]
    },
    rugby: {
      name: 'Rugby',
      halfLength: 40,
      periods: ['1H', 'HT', '2H', 'ET'],
      extraTimeStart: 80,
      scoring: [
        { type: 'try', label: 'Try', points: 5, emoji: '🏉' },
        { type: 'conversion', label: 'Conversion', points: 2, emoji: '🥅' },
        { type: 'penalty', label: 'Penalty', points: 3, emoji: '🎯' },
        { type: 'dropgoal', label: 'Drop Goal', points: 3, emoji: '🦵' }
      ]
    },
    gaelic_football: {
      name: 'Gaelic Football',
      halfLength: 30,
      periods: ['1H', 'HT', '2H', 'ET'],
      extraTimeStart: 60,
      scoring: [
        { type: 'goal', label: 'Goal', points: 3, emoji: '🥅' },
        { type: 'point', label: 'Point', points: 1, emoji: '🏐' }
      ]
    },
    gaelic_football_intercounty: {
      name: 'Gaelic Football (Inter-County)',
      halfLength: 35,
      periods: ['1H', 'HT', '2H', 'ET'],
      extraTimeStart: 70,
      scoring: [
        { type: 'goal', label: 'Goal', points: 3, emoji: '🥅' },
        { type: 'point', label: 'Point', points: 1, emoji: '🏐' }
      ]
    },
    hurling: {
      name: 'Hurling / Camogie',
      halfLength: 30,
      periods: ['1H', 'HT', '2H', 'ET'],
      extraTimeStart: 60,
      scoring: [
        { type: 'goal', label: 'Goal', points: 3, emoji: '🥅' },
        { type: 'point', label: 'Point', points: 1, emoji: '🏑' }
      ]
    },
    hurling_intercounty: {
      name: 'Hurling / Camogie (Inter-County)',
      halfLength: 35,
      periods: ['1H', 'HT', '2H', 'ET'],
      extraTimeStart: 70,
      scoring: [
        { type: 'goal', label: 'Goal', points: 3, emoji: '🥅' },
        { type: 'point', label: 'Point', points: 1, emoji: '🏑' }
      ]
    }
  };
  
  // Match Clock State
  const [clockRunning, setClockRunning] = useState(false);
  const [matchSeconds, setMatchSeconds] = useState(0); // Total seconds elapsed
  const [clockPeriod, setClockPeriod] = useState('1H'); // 1H, HT, 2H, ET1, ET2, PEN
  const [matchSport, setMatchSport] = useState('soccer'); // Current sport type
  
  const [clubForm, setClubForm] = useState({
    name: '', slug: '', logo_url: '', subscription_tier: 'standard',
    primary_color: '#0F202D', secondary_color: '#CCEAFF', accent_color: '#FFFFFF',
    instagram_handle: '', league_id: ''
  });
  const [playerForm, setPlayerForm] = useState({ name: '', number: '', position: '', photo_url: '' });
  const [matchForm, setMatchForm] = useState({
    home_club_name: '', away_club_name: '', home_logo_url: '', away_logo_url: '',
    league_name: '', venue: '', match_time: '', sport: 'soccer'
  });
  
  // Get current sport config
  const getSportConfig = () => SPORT_CONFIGS[matchSport] || SPORT_CONFIGS.soccer;
  
  // Check if sport is GAA (Gaelic Football or Hurling)
  const isGAASport = () => ['gaelic_football', 'gaelic_football_intercounty', 'hurling', 'hurling_intercounty'].includes(matchSport);
  
  // Format GAA score display (Goals-Points)
  const formatGAAScore = (goals, points) => `${goals || 0}-${String(points || 0).padStart(2, '0')}`;
  
  // Match Clock Effect - runs every second when clock is active
  useEffect(() => {
    let interval;
    if (clockRunning) {
      interval = setInterval(() => {
        setMatchSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [clockRunning]);
  
  // Format clock display based on sport config
  const formatClock = () => {
    const config = getSportConfig();
    const halfLength = config.halfLength;
    const totalMinutes = Math.floor(matchSeconds / 60);
    const seconds = matchSeconds % 60;
    
    if (clockPeriod === 'HT') return 'HT';
    if (clockPeriod === 'PEN') return 'PEN';
    
    let displayMinute = totalMinutes;
    
    if (clockPeriod === '1H') {
      // First half: 0 to halfLength, then halfLength+X for stoppage
      if (totalMinutes >= halfLength) {
        const stoppage = totalMinutes - halfLength;
        return stoppage > 0 
          ? `${halfLength}+${stoppage}:${seconds.toString().padStart(2, '0')}`
          : `${halfLength}:${seconds.toString().padStart(2, '0')}`;
      }
    } else if (clockPeriod === '2H') {
      // Second half: halfLength to 2*halfLength
      const fullTimeMinute = halfLength * 2;
      if (totalMinutes >= fullTimeMinute) {
        const stoppage = totalMinutes - fullTimeMinute;
        return stoppage > 0 
          ? `${fullTimeMinute}+${stoppage}:${seconds.toString().padStart(2, '0')}`
          : `${fullTimeMinute}:${seconds.toString().padStart(2, '0')}`;
      }
    } else if (clockPeriod === 'ET1' || clockPeriod === 'ET') {
      // Extra time first half
      const etStart = config.extraTimeStart;
      const etHalf = 15; // Standard 15 min ET halves
      if (totalMinutes >= etStart + etHalf) {
        const stoppage = totalMinutes - (etStart + etHalf);
        return stoppage > 0 
          ? `${etStart + etHalf}+${stoppage}:${seconds.toString().padStart(2, '0')}`
          : `${etStart + etHalf}:${seconds.toString().padStart(2, '0')}`;
      }
    } else if (clockPeriod === 'ET2') {
      // Extra time second half
      const etStart = config.extraTimeStart;
      const etEnd = etStart + 30;
      if (totalMinutes >= etEnd) {
        const stoppage = totalMinutes - etEnd;
        return stoppage > 0 
          ? `${etEnd}+${stoppage}:${seconds.toString().padStart(2, '0')}`
          : `${etEnd}:${seconds.toString().padStart(2, '0')}`;
      }
    }
    
    return `${displayMinute}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Get current minute for graphics (simplified format)
  const getCurrentMinute = () => {
    const config = getSportConfig();
    const halfLength = config.halfLength;
    const totalMinutes = Math.floor(matchSeconds / 60);
    
    if (clockPeriod === '1H' && totalMinutes >= halfLength) {
      const stoppage = totalMinutes - halfLength;
      return stoppage > 0 ? `${halfLength}+${stoppage}` : `${halfLength}`;
    } else if (clockPeriod === '2H' && totalMinutes >= halfLength * 2) {
      const stoppage = totalMinutes - (halfLength * 2);
      return stoppage > 0 ? `${halfLength * 2}+${stoppage}` : `${halfLength * 2}`;
    } else if ((clockPeriod === 'ET1' || clockPeriod === 'ET') && totalMinutes >= config.extraTimeStart + 15) {
      const stoppage = totalMinutes - (config.extraTimeStart + 15);
      return stoppage > 0 ? `${config.extraTimeStart + 15}+${stoppage}` : `${config.extraTimeStart + 15}`;
    } else if (clockPeriod === 'ET2' && totalMinutes >= config.extraTimeStart + 30) {
      const stoppage = totalMinutes - (config.extraTimeStart + 30);
      return stoppage > 0 ? `${config.extraTimeStart + 30}+${stoppage}` : `${config.extraTimeStart + 30}`;
    }
    
    return totalMinutes.toString();
  };

  const loadClubs = useCallback(async () => {
    try {
      const res = await api.getClubs();
      setClubs(res.data);
    } catch (error) {
      console.error('Error loading clubs:', error);
    }
  }, []);

  const loadPlayers = useCallback(async (clubId) => {
    try {
      const res = await api.getPlayers(clubId);
      setPlayers(res.data);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  }, []);

  const loadMatches = useCallback(async (clubId) => {
    try {
      const res = await api.getClubMatches(clubId);
      setMatches(res.data);
    } catch (error) {
      console.error('Error loading matches:', error);
    }
  }, []);

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  useEffect(() => {
    if (selectedClub) {
      loadPlayers(selectedClub.id);
      loadMatches(selectedClub.id);
    }
  }, [selectedClub, loadPlayers, loadMatches]);

  const handleCreateClub = async (e) => {
    e.preventDefault();
    try {
      const res = await api.createClub({
        ...clubForm,
        slug: clubForm.name.toLowerCase().replace(/\s+/g, '-')
      });
      setClubs([...clubs, res.data]);
      setShowClubForm(false);
      setClubForm({ name: '', slug: '', logo_url: '', subscription_tier: 'standard',
        primary_color: '#0F202D', secondary_color: '#CCEAFF', accent_color: '#FFFFFF',
        instagram_handle: '', league_id: '' });
    } catch (error) {
      alert('Error creating club');
    }
  };

  const handleCreatePlayer = async (e) => {
    e.preventDefault();
    try {
      await api.createPlayer(selectedClub.id, {
        ...playerForm,
        club_id: selectedClub.id,
        number: playerForm.number ? parseInt(playerForm.number) : null
      });
      loadPlayers(selectedClub.id);
      setShowPlayerForm(false);
      setPlayerForm({ name: '', number: '', position: '', photo_url: '' });
    } catch (error) {
      alert('Error adding player');
    }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    try {
      await api.createMatch(selectedClub.id, {
        ...matchForm,
        club_id: selectedClub.id,
        home_club_id: selectedClub.id,
        away_club_id: 'opponent',
        match_time: new Date(matchForm.match_time).toISOString()
      });
      loadMatches(selectedClub.id);
      setShowMatchForm(false);
      setMatchForm({ home_club_name: '', away_club_name: '', home_logo_url: '', away_logo_url: '',
        league_name: '', venue: '', match_time: '' });
    } catch (error) {
      alert('Error creating match');
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (window.confirm('Remove this player?')) {
      await api.deletePlayer(playerId);
      loadPlayers(selectedClub.id);
    }
  };

  const openMatchControl = (match) => {
    setActiveMatch(match);
    setShowMatchControl(true);
    setGeneratedGraphic(null);
    // Set sport type from match (default to soccer)
    setMatchSport(match.sport || 'soccer');
    const config = SPORT_CONFIGS[match.sport || 'soccer'];
    // Reset clock based on match status
    setClockRunning(false);
    if (match.status === 'scheduled') {
      setMatchSeconds(0);
      setClockPeriod('1H');
    } else if (match.status === 'live') {
      setClockPeriod('1H');
    } else if (match.status === 'halftime') {
      setMatchSeconds(config.halfLength * 60);
      setClockPeriod('HT');
    } else if (match.status === 'secondhalf') {
      setMatchSeconds(config.halfLength * 60);
      setClockPeriod('2H');
    } else if (match.status === 'extratime') {
      setMatchSeconds(config.extraTimeStart * 60);
      setClockPeriod('ET1');
    }
  };

  const handleGenerateGraphic = async (type, scorerName = null, minute = null, playerPhotoUrl = null) => {
    const currentMinute = minute || getCurrentMinute();
    try {
      const res = await api.generateGraphic(activeMatch.id, {
        match_id: activeMatch.id,
        graphic_type: type,
        scorer_name: scorerName,
        minute: currentMinute,
        player_photo_url: playerPhotoUrl
      });
      setGeneratedGraphic(res.data);
    } catch (error) {
      alert('Error generating graphic');
      console.error(error);
    }
  };

  // State for goal scorer selection
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [goalTeam, setGoalTeam] = useState('home');
  const [selectedScorer, setSelectedScorer] = useState(null);
  const [scoringType, setScoringType] = useState('goal'); // For multi-point sports

  // Handle adding score - for GAA, instant; for others, show player selector
  const handleAddGoal = async (team, scoreType = 'goal') => {
    const scoreConfig = getSportConfig().scoring.find(s => s.type === scoreType) || { type: 'goal', points: 1 };
    
    // For GAA sports, add score instantly without player selection
    if (isGAASport()) {
      const minute = getCurrentMinute();
      const isGaaGoal = scoreType === 'goal'; // True for goal (3pts), false for point (1pt)
      try {
        const res = await api.addGoal(activeMatch.id, null, '', minute, team, scoreType, scoreConfig.points, isGaaGoal);
        setActiveMatch(res.data);
        // Don't auto-generate graphic for GAA instant scoring
      } catch (error) {
        alert('Error adding score');
      }
    } else {
      // For non-GAA sports, show player selection dialog
      setGoalTeam(team);
      setScoringType(scoreType);
      setSelectedScorer(null);
      setShowGoalDialog(true);
    }
  };
  
  const confirmGoal = async () => {
    if (!selectedScorer) {
      alert('Please select a scorer');
      return;
    }
    const minute = getCurrentMinute();
    const scoreConfig = getSportConfig().scoring.find(s => s.type === scoringType) || { type: 'goal', points: 1 };
    try {
      const res = await api.addGoal(activeMatch.id, selectedScorer.id, selectedScorer.name, minute, goalTeam, scoringType, scoreConfig.points, false);
      setActiveMatch(res.data);
      setShowGoalDialog(false);
      // Auto-generate goal graphic with player photo
      await handleGenerateGraphic('goal', selectedScorer.name, minute, selectedScorer.photo_url);
    } catch (error) {
      alert('Error adding goal');
    }
  };

  // Remove a goal/score
  const handleRemoveGoal = async (goalId) => {
    if (!window.confirm('Remove this score?')) return;
    try {
      const res = await api.removeGoal(activeMatch.id, goalId);
      setActiveMatch(res.data);
    } catch (error) {
      alert('Error removing score');
    }
  };

  // Edit score directly
  const handleEditScore = async (field, delta) => {
    const currentValue = activeMatch[field] || 0;
    const newValue = Math.max(0, currentValue + delta);
    try {
      const res = await api.editScore(activeMatch.id, { [field]: newValue });
      setActiveMatch(res.data);
    } catch (error) {
      alert('Error updating score');
    }
  };

  const handleUpdateStatus = async (status) => {
    const config = getSportConfig();
    try {
      const minute = getCurrentMinute();
      const res = await api.updateMatchStatus(
        activeMatch.id, 
        status,
        minute
      );
      setActiveMatch(res.data);
      
      // Update clock based on status and sport config
      if (status === 'live') {
        setClockRunning(true);
        setClockPeriod('1H');
        setMatchSeconds(0);
        await handleGenerateGraphic('kickoff');
      } else if (status === 'halftime') {
        setClockRunning(false);
        setClockPeriod('HT');
        await handleGenerateGraphic('halftime');
      } else if (status === 'secondhalf') {
        setClockRunning(true);
        setClockPeriod('2H');
        setMatchSeconds(config.halfLength * 60); // Start at half length (45 for soccer, 30 for GAA, etc.)
      } else if (status === 'extratime') {
        setClockRunning(true);
        setClockPeriod('ET1');
        setMatchSeconds(config.extraTimeStart * 60); // Start at extra time (90 for soccer, 60 for GAA, etc.)
        await handleGenerateGraphic('extratime');
      } else if (status === 'penalties') {
        setClockRunning(false);
        setClockPeriod('PEN');
        await handleGenerateGraphic('penalties');
      } else if (status === 'finished') {
        setClockRunning(false);
        await handleGenerateGraphic('fulltime');
      }
    } catch (error) {
      alert('Error updating status');
    }
  };

  const handleAddCard = async (cardType) => {
    const playerName = prompt('Player name:');
    const minute = getCurrentMinute();
    const team = prompt('Team (home/away):') || 'home';
    if (playerName) {
      try {
        await api.addMatchEvent(activeMatch.id, cardType, playerName, minute, team);
        // Refresh match data
        const res = await api.getMatch(activeMatch.id);
        setActiveMatch(res.data);
        // Auto-generate card graphic
        await handleGenerateGraphic(cardType, playerName, minute);
      } catch (error) {
        alert('Error adding card');
      }
    }
  };

  const shareToInstagram = async () => {
    if (generatedGraphic) {
      // Create download link
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${generatedGraphic.base64}`;
      link.download = generatedGraphic.filename;
      link.click();
      
      // Try to open Instagram (works on mobile)
      setTimeout(() => {
        window.open('instagram://camera', '_blank');
      }, 500);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">
          <Shield className="w-6 h-6 inline mr-2" /> Club Score Tracking
        </h2>
        <Button onClick={() => setShowClubForm(true)} className="bg-sky-400 hover:bg-sky-500 text-gray-900">
          <Plus className="w-4 h-4 mr-2" /> Add Club
        </Button>
      </div>

      {/* Club Selection */}
      <div className="grid grid-cols-4 gap-4">
        {clubs.map(club => (
          <Card 
            key={club.id} 
            className={`bg-gray-800/50 border-sky-900/30 cursor-pointer transition-all ${selectedClub?.id === club.id ? 'ring-2 ring-sky-400' : ''}`}
            onClick={() => setSelectedClub(club)}
          >
            <CardContent className="p-4 text-center">
              {club.logo_url ? (
                <img src={resolveMediaUrl(club.logo_url)} alt={club.name} className="w-16 h-16 mx-auto rounded-full object-cover mb-2" />
              ) : (
                <Shield className="w-16 h-16 mx-auto text-gray-600 mb-2" />
              )}
              <h3 className="text-white font-semibold">{club.name}</h3>
              <Badge className={club.subscription_tier === 'premium' ? 'bg-yellow-500' : 'bg-sky-500'}>
                {club.subscription_tier.toUpperCase()}
              </Badge>
            </CardContent>
          </Card>
        ))}
        {clubs.length === 0 && (
          <div className="col-span-4 text-center py-8 text-gray-400">
            No clubs yet. Add a club to get started with score tracking!
          </div>
        )}
      </div>

      {/* Selected Club Details */}
      {selectedClub && (
        <div className="space-y-6">
          {/* Club Header */}
          <Card className="bg-gray-800/50 border-sky-900/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectedClub.logo_url ? (
                    <img src={selectedClub.logo_url} alt={selectedClub.name} className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <Shield className="w-20 h-20 text-gray-600" />
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedClub.name}</h2>
                    <Badge className={selectedClub.subscription_tier === 'premium' ? 'bg-yellow-500' : 'bg-sky-500'}>
                      {selectedClub.subscription_tier.toUpperCase()}
                    </Badge>
                    {selectedClub.subscription_tier === 'premium' && (
                      <div className="flex gap-2 mt-2">
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: selectedClub.primary_color }}></div>
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: selectedClub.secondary_color }}></div>
                        <span className="text-gray-400 text-sm">Custom Colors</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowPlayerForm(true)} variant="outline" className="border-gray-600">
                    <UserCircle className="w-4 h-4 mr-2" /> Add Player
                  </Button>
                  <Button onClick={() => setShowMatchForm(true)} className="bg-sky-400 hover:bg-sky-500 text-gray-900">
                    <Plus className="w-4 h-4 mr-2" /> New Match
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Players Roster */}
          <Card className="bg-gray-800/50 border-sky-900/30">
            <CardHeader>
              <CardTitle className="text-white">Player Roster ({players.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-6 gap-4">
                {players.map(player => (
                  <div key={player.id} className="text-center group relative">
                    {player.photo_url ? (
                      <img src={player.photo_url} alt={player.name} className="w-16 h-16 mx-auto rounded-full object-cover mb-2" />
                    ) : (
                      <UserCircle className="w-16 h-16 mx-auto text-gray-600 mb-2" />
                    )}
                    <p className="text-white text-sm font-medium">{player.name}</p>
                    <p className="text-gray-400 text-xs">#{player.number} {player.position}</p>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 w-6 h-6 p-0"
                      onClick={() => handleDeletePlayer(player.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                {players.length === 0 && (
                  <div className="col-span-6 text-center py-4 text-gray-400">
                    No players added yet. Add players to use in goal graphics.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Matches */}
          <Card className="bg-gray-800/50 border-sky-900/30">
            <CardHeader>
              <CardTitle className="text-white">Matches</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {matches.map(match => (
                <div key={match.id} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <Badge className={
                      match.status === 'live' ? 'bg-red-500' :
                      match.status === 'finished' ? 'bg-gray-500' :
                      match.status === 'halftime' ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }>
                      {match.status.toUpperCase()}
                    </Badge>
                    <div>
                      <p className="text-white font-semibold">
                        {match.home_club_name} {match.home_score} - {match.away_score} {match.away_club_name}
                      </p>
                      <p className="text-gray-400 text-sm">{match.league_name} • {formatDate(match.match_time)}</p>
                    </div>
                  </div>
                  <Button onClick={() => openMatchControl(match)} className="bg-sky-400 hover:bg-sky-500 text-gray-900">
                    <Play className="w-4 h-4 mr-2" /> Control Match
                  </Button>
                </div>
              ))}
              {matches.length === 0 && (
                <div className="text-center py-4 text-gray-400">
                  No matches yet. Create a match to start tracking scores!
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Club Form */}
      <Dialog open={showClubForm} onOpenChange={setShowClubForm}>
        <DialogContent className="bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Club</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClub} className="space-y-4">
            <div>
              <Label className="text-gray-300">Club Name</Label>
              <Input value={clubForm.name} onChange={(e) => setClubForm({...clubForm, name: e.target.value})} 
                className="bg-gray-700 border-gray-600 text-white" required />
            </div>
            <div>
              <Label className="text-gray-300">Logo URL</Label>
              <Input value={clubForm.logo_url} onChange={(e) => setClubForm({...clubForm, logo_url: e.target.value})} 
                className="bg-gray-700 border-gray-600 text-white" placeholder="https://..." />
            </div>
            <div>
              <Label className="text-gray-300">Subscription Tier</Label>
              <Select value={clubForm.subscription_tier} onValueChange={(v) => setClubForm({...clubForm, subscription_tier: v})}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="standard" className="text-white">Standard - NLS Colors</SelectItem>
                  <SelectItem value="premium" className="text-white">Premium - Custom Colors</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {clubForm.subscription_tier === 'premium' && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Primary Color</Label>
                  <Input type="color" value={clubForm.primary_color} 
                    onChange={(e) => setClubForm({...clubForm, primary_color: e.target.value})} 
                    className="bg-gray-700 border-gray-600 h-10" />
                </div>
                <div>
                  <Label className="text-gray-300">Secondary Color</Label>
                  <Input type="color" value={clubForm.secondary_color} 
                    onChange={(e) => setClubForm({...clubForm, secondary_color: e.target.value})} 
                    className="bg-gray-700 border-gray-600 h-10" />
                </div>
                <div>
                  <Label className="text-gray-300">Accent Color</Label>
                  <Input type="color" value={clubForm.accent_color} 
                    onChange={(e) => setClubForm({...clubForm, accent_color: e.target.value})} 
                    className="bg-gray-700 border-gray-600 h-10" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-gray-300">Instagram Handle</Label>
              <Input value={clubForm.instagram_handle} onChange={(e) => setClubForm({...clubForm, instagram_handle: e.target.value})} 
                className="bg-gray-700 border-gray-600 text-white" placeholder="@yourclub" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-sky-400 hover:bg-sky-500 text-gray-900">Create Club</Button>
              <Button type="button" variant="outline" className="border-gray-600" onClick={() => setShowClubForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Player Form */}
      <Dialog open={showPlayerForm} onOpenChange={setShowPlayerForm}>
        <DialogContent className="bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Add Player</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePlayer} className="space-y-4">
            <div>
              <Label className="text-gray-300">Player Name</Label>
              <Input value={playerForm.name} onChange={(e) => setPlayerForm({...playerForm, name: e.target.value})} 
                className="bg-gray-700 border-gray-600 text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Number</Label>
                <Input type="number" value={playerForm.number} onChange={(e) => setPlayerForm({...playerForm, number: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Position</Label>
                <Input value={playerForm.position} onChange={(e) => setPlayerForm({...playerForm, position: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" placeholder="Forward, Midfielder..." />
              </div>
            </div>
            <PlayerPhotoCrop
              value={playerForm.photo_url}
              onChange={(url) => setPlayerForm({...playerForm, photo_url: url})}
            />
            <div className="flex gap-2">
              <Button type="submit" className="bg-sky-400 hover:bg-sky-500 text-gray-900">Add Player</Button>
              <Button type="button" variant="outline" className="border-gray-600" onClick={() => setShowPlayerForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Match Form */}
      <Dialog open={showMatchForm} onOpenChange={setShowMatchForm}>
        <DialogContent className="bg-gray-900 border-sky-900/50">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Match</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMatch} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Home Team</Label>
                <Input value={matchForm.home_club_name} onChange={(e) => setMatchForm({...matchForm, home_club_name: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Away Team</Label>
                <Input value={matchForm.away_club_name} onChange={(e) => setMatchForm({...matchForm, away_club_name: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Home Logo URL</Label>
                <Input value={matchForm.home_logo_url} onChange={(e) => setMatchForm({...matchForm, home_logo_url: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Away Logo URL</Label>
                <Input value={matchForm.away_logo_url} onChange={(e) => setMatchForm({...matchForm, away_logo_url: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">League/Competition</Label>
                <Input value={matchForm.league_name} onChange={(e) => setMatchForm({...matchForm, league_name: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Venue</Label>
                <Input value={matchForm.venue} onChange={(e) => setMatchForm({...matchForm, venue: e.target.value})} 
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Match Date & Time</Label>
              <Input type="datetime-local" value={matchForm.match_time} 
                onChange={(e) => setMatchForm({...matchForm, match_time: e.target.value})} 
                className="bg-gray-700 border-gray-600 text-white" required />
            </div>
            <div>
              <Label className="text-gray-300">Sport</Label>
              <Select value={matchForm.sport} onValueChange={(v) => setMatchForm({...matchForm, sport: v})}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select sport" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {Object.entries(SPORT_CONFIGS).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-gray-700">
                      {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-sky-400 hover:bg-sky-500 text-gray-900">Create Match</Button>
              <Button type="button" variant="outline" className="border-gray-600" onClick={() => setShowMatchForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Match Control Panel */}
      <Dialog open={showMatchControl} onOpenChange={setShowMatchControl}>
        <DialogContent className="max-w-4xl bg-gray-900 border-sky-900/50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">Match Center</DialogTitle>
          </DialogHeader>
          {activeMatch && (
            <div className="space-y-6">
              {/* Score Display with Live Clock */}
              <div className="text-center p-6 bg-gray-800 rounded-lg">
                <p className="text-gray-400 mb-2">{activeMatch.league_name}</p>
                
                {/* Live Clock Display */}
                <div className="mb-4">
                  <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-lg ${clockRunning ? 'bg-red-600 animate-pulse' : 'bg-gray-700'}`}>
                    <Clock className="w-6 h-6 text-white" />
                    <span className="text-4xl font-mono font-bold text-white">{formatClock()}</span>
                    {clockRunning && <span className="text-xs text-white/70">LIVE</span>}
                  </div>
                  <div className="flex justify-center gap-2 mt-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className={`border-gray-600 ${clockRunning ? 'text-red-400' : 'text-green-400'}`}
                      onClick={() => setClockRunning(!clockRunning)}
                    >
                      {clockRunning ? '⏸️ Pause' : '▶️ Start'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-gray-600 text-gray-400"
                      onClick={() => { setMatchSeconds(0); setClockPeriod('1H'); }}
                    >
                      🔄 Reset
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    {activeMatch.home_logo_url && (
                      <img src={activeMatch.home_logo_url} alt="" className="w-16 h-16 mx-auto mb-2 object-contain" />
                    )}
                    <p className="text-white text-xl font-semibold">{activeMatch.home_club_name}</p>
                  </div>
                  <div className="text-center">
                    {/* GAA Score Format: Goals-Points | Goals-Points */}
                    {isGAASport() ? (
                      <div className="text-5xl font-bold text-white font-mono">
                        {formatGAAScore(activeMatch.home_goals, activeMatch.home_points)}
                        <span className="text-gray-400 mx-3">|</span>
                        {formatGAAScore(activeMatch.away_goals, activeMatch.away_points)}
                      </div>
                    ) : (
                      <div className="text-6xl font-bold text-white">
                        {activeMatch.home_score} - {activeMatch.away_score}
                      </div>
                    )}
                    {/* Total score for GAA */}
                    {isGAASport() && (
                      <p className="text-gray-400 text-sm mt-1">
                        Total: {activeMatch.home_score || 0} - {activeMatch.away_score || 0}
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    {activeMatch.away_logo_url && (
                      <img src={activeMatch.away_logo_url} alt="" className="w-16 h-16 mx-auto mb-2 object-contain" />
                    )}
                    <p className="text-white text-xl font-semibold">{activeMatch.away_club_name}</p>
                  </div>
                </div>
                <Badge className={
                  activeMatch.status === 'live' ? 'bg-red-500 mt-4' :
                  activeMatch.status === 'halftime' ? 'bg-yellow-500 mt-4' :
                  activeMatch.status === 'secondhalf' ? 'bg-red-500 mt-4' :
                  activeMatch.status === 'extratime' ? 'bg-orange-500 mt-4' :
                  activeMatch.status === 'penalties' ? 'bg-purple-500 mt-4' :
                  activeMatch.status === 'finished' ? 'bg-gray-500 mt-4' :
                  'bg-blue-500 mt-4'
                }>
                  {activeMatch.status.toUpperCase().replace('SECONDHALF', '2ND HALF')}
                </Badge>
              </div>

              {/* Match Status Controls */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-3">Match Status</h4>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => handleUpdateStatus('live')} size="sm" className="bg-green-600 hover:bg-green-700">
                    ⏱️ Kickoff
                  </Button>
                  <Button onClick={() => handleUpdateStatus('halftime')} size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                    ⏸️ Halftime
                  </Button>
                  <Button onClick={() => handleUpdateStatus('secondhalf')} size="sm" className="bg-green-600 hover:bg-green-700">
                    ▶️ 2nd Half
                  </Button>
                  <Button onClick={() => handleUpdateStatus('extratime')} size="sm" className="bg-orange-600 hover:bg-orange-700">
                    ⏱️ Extra Time
                  </Button>
                  <Button onClick={() => handleUpdateStatus('penalties')} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    🎯 Penalties
                  </Button>
                  <Button onClick={() => handleUpdateStatus('finished')} size="sm" className="bg-gray-600 hover:bg-gray-700">
                    🏁 Full Time
                  </Button>
                </div>
              </div>

              {/* Sport Selector & Info */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-semibold mb-1">Sport Type</h4>
                    <p className="text-gray-400 text-sm">
                      {getSportConfig().name} • {getSportConfig().halfLength} min halves
                    </p>
                  </div>
                  <Select value={matchSport} onValueChange={setMatchSport}>
                    <SelectTrigger className="w-48 bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {Object.entries(SPORT_CONFIGS).map(([key, config]) => (
                        <SelectItem key={key} value={key} className="text-white hover:bg-gray-700">
                          {config.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Scoring - Sport Specific */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-3">🏆 {activeMatch.home_club_name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {getSportConfig().scoring.map(score => (
                      <Button 
                        key={score.type}
                        onClick={() => handleAddGoal('home', score.type)}
                        size="sm"
                        className="bg-sky-400 hover:bg-sky-500 text-gray-900"
                      >
                        {score.emoji} {score.label} (+{score.points})
                      </Button>
                    ))}
                  </div>
                  {/* GAA Quick Score Buttons */}
                  {isGAASport() && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-700">
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-300" onClick={() => handleEditScore('home_goals', -1)}>-G</Button>
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-300" onClick={() => handleEditScore('home_points', -1)}>-P</Button>
                    </div>
                  )}
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-3">🏆 {activeMatch.away_club_name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {getSportConfig().scoring.map(score => (
                      <Button 
                        key={score.type}
                        onClick={() => handleAddGoal('away', score.type)}
                        size="sm"
                        variant="outline"
                        className="border-sky-400 text-sky-400"
                      >
                        {score.emoji} {score.label} (+{score.points})
                      </Button>
                    ))}
                  </div>
                  {/* GAA Quick Score Buttons */}
                  {isGAASport() && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-700">
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-300" onClick={() => handleEditScore('away_goals', -1)}>-G</Button>
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-300" onClick={() => handleEditScore('away_points', -1)}>-P</Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Score Correction */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-3">✏️ Score Correction</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-2">{activeMatch.home_club_name}</p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEditScore('home_score', -1)}>-</Button>
                      <span className="text-white font-bold text-xl w-12 text-center">{activeMatch.home_score || 0}</span>
                      <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEditScore('home_score', 1)}>+</Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">{activeMatch.away_club_name}</p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEditScore('away_score', -1)}>-</Button>
                      <span className="text-white font-bold text-xl w-12 text-center">{activeMatch.away_score || 0}</span>
                      <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEditScore('away_score', 1)}>+</Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-3">🃏 Cards</h4>
                <div className="flex gap-2">
                  <Button onClick={() => handleAddCard('yellowcard')} size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black">
                    🟨 Yellow Card
                  </Button>
                  <Button onClick={() => handleAddCard('redcard')} size="sm" className="bg-red-600 hover:bg-red-700">
                    🟥 Red Card
                  </Button>
                </div>
              </div>

              {/* Goal Scorers with Remove Option */}
              {activeMatch.goal_scorers?.length > 0 && (
                <div className="bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Scorers</h4>
                  <div className="space-y-2">
                    {activeMatch.goal_scorers.map((goal, idx) => (
                      <div key={goal.id || idx} className="flex items-center justify-between bg-gray-700 rounded px-3 py-2">
                        <span className="text-gray-300">
                          {getSportConfig().scoring.find(s => s.type === (goal.score_type || 'goal'))?.emoji || '⚽'} 
                          {goal.player_name || 'Unknown'} ({goal.minute}&apos;) - {goal.team === 'home' ? activeMatch.home_club_name : activeMatch.away_club_name}
                          <span className="text-gray-500 text-xs ml-2">+{goal.points || 1}</span>
                        </span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-7 px-2"
                          onClick={() => handleRemoveGoal(goal.id)}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated Graphic Preview */}
              {generatedGraphic && (
                <div className="bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-3">📸 Generated Graphic</h4>
                  <div className="flex gap-4">
                    <img 
                      src={`data:image/png;base64,${generatedGraphic.base64}`} 
                      alt="Match graphic" 
                      className="w-48 h-48 object-cover rounded-lg"
                    />
                    <div className="flex flex-col gap-2 flex-1">
                      <Button onClick={shareToInstagram} className="bg-gradient-to-r from-purple-500 to-pink-500">
                        <Share2 className="w-4 h-4 mr-2" /> Share to Instagram
                      </Button>
                      <Button variant="outline" className="border-gray-600" asChild>
                        <a href={`data:image/png;base64,${generatedGraphic.base64}`} download={generatedGraphic.filename}>
                          <Download className="w-4 h-4 mr-2" /> Download Image
                        </a>
                      </Button>
                      <p className="text-gray-400 text-xs mt-2">
                        💡 Download the image, then open Instagram to share.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Graphic Generation */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-3">🎨 Generate Graphics</h4>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => handleGenerateGraphic('kickoff')} size="sm" variant="outline" className="border-gray-600">
                    Kickoff
                  </Button>
                  <Button onClick={() => handleGenerateGraphic('goal')} size="sm" variant="outline" className="border-gray-600">
                    Goal
                  </Button>
                  <Button onClick={() => handleGenerateGraphic('halftime')} size="sm" variant="outline" className="border-gray-600">
                    Halftime
                  </Button>
                  <Button onClick={() => handleGenerateGraphic('fulltime')} size="sm" variant="outline" className="border-gray-600">
                    Full Time
                  </Button>
                  <Button onClick={() => handleGenerateGraphic('extratime')} size="sm" variant="outline" className="border-gray-600">
                    Extra Time
                  </Button>
                  <Button onClick={() => handleGenerateGraphic('penalties')} size="sm" variant="outline" className="border-gray-600">
                    Penalties
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Goal Scorer Selection Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="bg-gray-900 border-sky-900/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {getSportConfig().scoring.find(s => s.type === scoringType)?.emoji || '⚽'} Select Scorer - {goalTeam === 'home' ? activeMatch?.home_club_name : activeMatch?.away_club_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-gray-400 text-sm">
                Current time: <span className="text-sky-300 font-mono">{getCurrentMinute()}&apos;</span>
              </p>
              <Badge className="bg-sky-600">
                {getSportConfig().scoring.find(s => s.type === scoringType)?.label || 'Goal'} (+{getSportConfig().scoring.find(s => s.type === scoringType)?.points || 1})
              </Badge>
            </div>
            
            {/* Player List */}
            <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
              {players.map(player => (
                <div
                  key={player.id}
                  onClick={() => setSelectedScorer(player)}
                  className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                    selectedScorer?.id === player.id 
                      ? 'bg-sky-600 border-2 border-sky-400' 
                      : 'bg-gray-800 hover:bg-gray-700 border-2 border-transparent'
                  }`}
                >
                  {player.photo_url ? (
                    <img src={player.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white font-bold">
                      {player.number}
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium text-sm">{player.name}</p>
                    <p className="text-gray-400 text-xs">#{player.number} • {player.position}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {players.length === 0 && (
              <p className="text-gray-500 text-center py-4">No players in roster. Add players first.</p>
            )}
            
            {/* Quick Add Option */}
            <div className="border-t border-gray-700 pt-4">
              <Button 
                variant="outline" 
                className="w-full border-gray-600 text-gray-300"
                onClick={() => {
                  const name = prompt('Enter scorer name:');
                  if (name) {
                    setSelectedScorer({ id: null, name, photo_url: null, number: '?', position: '' });
                  }
                }}
              >
                + Add Unlisted Player
              </Button>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={confirmGoal}
                disabled={!selectedScorer}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                ⚽ Confirm Goal
              </Button>
              <Button 
                variant="outline" 
                className="border-gray-600"
                onClick={() => setShowGoalDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};




export { AdminClubsManager };
